import React, { useMemo } from "react";
import { Box, Typography, Grid, CircularProgress, Stack } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApplicationPackage } from "../../hooks/useApplicationPackages";
import { getServicesByPackage } from "../../hooks/usePackageServiceMapping";
import { colors } from "../../colors";

interface PackageCardsViewProps {
  packages: ApplicationPackage[];
}

const CARD_THEMES = [
  { color: "#f0a030", shadow: "rgba(240, 160, 48, 0.4)" },
  { color: "#e85d4c", shadow: "rgba(232, 93, 76, 0.4)" },
  { color: "#4ecdc4", shadow: "rgba(78, 205, 196, 0.4)" },
  { color: "#b19cd9", shadow: "rgba(177, 156, 217, 0.4)" },
  { color: "#5b8def", shadow: "rgba(91, 141, 239, 0.4)" },
  { color: "#e84393", shadow: "rgba(232, 67, 147, 0.4)" },
  { color: "#00b894", shadow: "rgba(0, 184, 148, 0.4)" },
  { color: "#fd9644", shadow: "rgba(253, 150, 68, 0.4)" },
  { color: "#6c5ce7", shadow: "rgba(108, 92, 231, 0.4)" },
  { color: "#e17055", shadow: "rgba(225, 112, 85, 0.4)" },
];

const PackageCardsView: React.FC<PackageCardsViewProps> = ({ packages }) => {
  const { t } = useTranslation();

  const displayPackages = useMemo(() => {
    const active = packages.filter((p) => p.active !== false);
    const list = active.length > 0 ? active : packages;
    return [...list].sort((a, b) => Number(a.price ?? 0) - Number(b.price ?? 0));
  }, [packages]);

  const mappingQueries = useQueries({
    queries: displayPackages.map((pkg) => ({
      queryKey: ["package-service-mapping", pkg.id],
      queryFn: () => getServicesByPackage(pkg.id!),
      enabled: !!pkg.id,
    })),
  });

  const isLoading = mappingQueries.some((q) => q.isLoading);

  const formatPrice = (price?: number) => {
    const value = Number(price ?? 0);
    if (value === 0) {
      return {
        amount: t("free"),
        period: "",
        isFree: true,
      };
    }
    return {
      amount: `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`,
      period: t("per_year_label") || "PER YEAR",
      isFree: false,
    };
  };

  if (displayPackages.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          px: 3,
          borderRadius: 3,
          bgcolor: "#f0f0f0",
          border: "1px dashed #ccc",
        }}
      >
        <Inventory2OutlinedIcon sx={{ fontSize: 48, color: "#999", mb: 1 }} />
        <Typography color="text.secondary" fontWeight={500}>
          {t("no_packages_available")}
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress sx={{ color: colors.created }} />
      </Box>
    );
  }

  return (
    <Grid
      container
      spacing={3}
      justifyContent="center"
      sx={{ py: 4, px: 2, overflow: "visible" }}
    >
      {displayPackages.map((pkg, index) => {
        const services = mappingQueries[index]?.data?.servicesList ?? [];
        const { amount, period, isFree } = formatPrice(pkg.price);
        const isInactive = pkg.active === false;
        const theme = CARD_THEMES[index % CARD_THEMES.length];

        return (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            key={pkg.id}
            sx={{ display: "flex", overflow: "visible" }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                minHeight: 320,
                borderRadius: "14px",
                bgcolor: "#fff",
                opacity: isInactive ? 0.65 : 1,
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                overflow: "visible",
                cursor: "default",
                transform: "scale(1)",
                transformOrigin: "center center",
                transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s ease",
                "&:hover": {
                  transform: "scale(1.1)",
                  zIndex: 10,
                  boxShadow: `0 20px 40px ${theme.shadow}`,
                },
              }}
            >
              {/* Colored header with diagonal bottom edge */}
              <Box
                sx={{
                  position: "relative",
                  bgcolor: theme.color,
                  pt: 3.5,
                  pb: 5,
                  px: 2.5,
                  minHeight: 130,
                  clipPath: "polygon(0 0, 100% 0, 100% 72%, 0 100%)",
                  borderRadius: "14px 14px 0 0",
                }}
              >
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: "1.35rem",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    lineHeight: 1.2,
                    pr: 12,
                  }}
                >
                  {pkg.name}
                </Typography>
              </Box>

              {/* Price circle badge */}
              <Box
                sx={{
                  position: "absolute",
                  top: 78,
                  right: 16,
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  bgcolor: "#fff",
                  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.18)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                }}
              >
                {!isFree && (
                  <Typography
                    sx={{
                      color: "#555",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      textAlign: "center",
                      lineHeight: 1.2,
                      px: 0.5,
                    }}
                  >
                    {t("only")}
                  </Typography>
                )}
                <Typography
                  sx={{
                    color: theme.color,
                    fontWeight: 800,
                    fontSize: isFree ? "1.35rem" : "1.25rem",
                    lineHeight: 1.1,
                    my: isFree ? 0 : 0.25,
                  }}
                >
                  {amount}
                </Typography>
                {!isFree && period && (
                  <Typography
                    sx={{
                      color: "#555",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      textAlign: "center",
                      lineHeight: 1.2,
                      px: 0.5,
                    }}
                  >
                    {period}
                  </Typography>
                )}
              </Box>

              {/* White body */}
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "0 0 14px 14px",
                  pt: 6,
                  pb: 3,
                  px: 2.5,
                  minHeight: 170,
                }}
              >
                {pkg.description && (
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#1a1a1a",
                      fontSize: "0.95rem",
                      fontWeight: 700,
                      lineHeight: 1.5,
                      mb: 2,
                      pr: 1,
                    }}
                  >
                    {pkg.description}
                  </Typography>
                )}

                {services.length > 0 ? (
                  <Stack spacing={1.75}>
                    {services.map((service) => (
                      <Stack
                        key={service.id}
                        direction="row"
                        spacing={1.25}
                        alignItems="flex-start"
                      >
                        <CheckIcon
                          sx={{
                            fontSize: 20,
                            color: theme.color,
                            mt: 0.2,
                            flexShrink: 0,
                            fontWeight: 700,
                          }}
                        />
                        <Typography
                          variant="body1"
                          sx={{
                            color: "#1a1a1a",
                            fontSize: "0.95rem",
                            fontWeight: 500,
                            lineHeight: 1.5,
                          }}
                        >
                          {service.name}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: "#555", fontStyle: "italic", fontSize: "0.95rem" }}
                  >
                    {t("no_services_in_package")}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default PackageCardsView;
